import { createContext, useContext, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import type { AdminMeDto, DemoStatusDto } from "@gazelab/shared";
import { api, ApiError } from "../../services/api.js";
import { ErrorAlert, Spinner } from "../../components/ui/index.js";
import { BrandMark } from "../../components/ui/Brand.js";
import { Icon } from "../../components/ui/Icon.js";
import { SidebarSlotContext } from "../../components/ui/Layout.js";
import { DemoBanner } from "../../components/ui/DemoBanner.js";
import { TourProvider, useTour } from "../../components/tour/Tour.js";
import { tourFor } from "../../components/tour/tours.js";
import { useWhatsNew, WhatsNewDialog } from "../../features/whatsNew/WhatsNew.js";

const TOUR_SEEN_KEY = "gazelab:tour-seen";

/** Aviso de novedades (dentro del proveedor del recorrido para poder abrirlo desde el aviso). */
function WhatsNewHost({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { start } = useTour();
  const location = useLocation();
  return <WhatsNewDialog open={open} onClose={onClose} onTour={() => start(tourFor(location.pathname))} />;
}

/** Botón que abre el recorrido guiado de la pantalla actual (marca un punto hasta usarlo una vez). */
function TourButton({ variant }: { variant: "sidebar" | "mobile" }) {
  const { start } = useTour();
  const location = useLocation();
  const [seen, setSeen] = useState(() => {
    try {
      return localStorage.getItem(TOUR_SEEN_KEY) === "1";
    } catch {
      return true;
    }
  });
  function open() {
    start(tourFor(location.pathname));
    try {
      localStorage.setItem(TOUR_SEEN_KEY, "1");
    } catch {
      // Sin almacenamiento: solo se pierde el aviso de «nuevo».
    }
    setSeen(true);
  }
  if (variant === "mobile") {
    return (
      <button type="button" onClick={open} className="text-sm text-aqua" data-testid="tour-button-mobile">
        Recorrido
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={open}
      className="relative flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-medium text-white/70 transition-colors hover:bg-white/5 hover:text-white"
      data-testid="tour-button"
      title="Recorrido guiado de esta pantalla"
    >
      <Icon name="help" /> Recorrido guiado
      {!seen && <span className="absolute right-3 top-1/2 h-2 w-2 -translate-y-1/2 animate-pulse rounded-full bg-aqua" aria-label="nuevo" />}
    </button>
  );
}

const AdminContext = createContext<AdminMeDto | null>(null);
export const useAdmin = () => useContext(AdminContext);

/** Estado del modo demo (null mientras carga). */
const DemoContext = createContext<DemoStatusDto | null>(null);
export const useDemo = () => useContext(DemoContext);
/** Texto de los botones de exportación deshabilitados en modo demo. */
export const DEMO_EXPORT_NOTE = "Exportaciones deshabilitadas en modo demo";
/** Acciones destructivas o sensibles bloqueadas en modo demo. */
export const DEMO_LOCK_NOTE = "No disponible en modo demo";

const navClass = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
    isActive ? "bg-white/10 text-aqua" : "text-white/70 hover:bg-white/5 hover:text-white"
  }`;

const mobileNavClass = ({ isActive }: { isActive: boolean }) =>
  `rounded-full px-3 py-1.5 text-sm font-medium ${isActive ? "bg-white/10 text-aqua" : "text-white/75"}`;

export function AdminLayout() {
  const [me, setMe] = useState<AdminMeDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [demo, setDemo] = useState<DemoStatusDto | null>(null);
  const whatsNew = useWhatsNew();
  const navigate = useNavigate();
  const location = useLocation();

  // Estado del modo demo (y su uso) al cargar y en cada cambio de página.
  useEffect(() => {
    if (!me) return;
    let cancelled = false;
    api
      .demoStatus()
      .then((d) => !cancelled && setDemo(d))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [me, location.pathname]);

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((m) => !cancelled && setMe(m))
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 401) {
          navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`, { replace: true });
        } else setError(e instanceof Error ? e.message : "No se pudo verificar la sesión");
      });
    return () => {
      cancelled = true;
    };
    // Solo al montar: la sesión se verifica una vez por carga del panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function logout() {
    try {
      await api.logout();
    } finally {
      navigate("/login", { replace: true });
    }
  }

  if (error)
    return (
      <div className="p-8">
        <ErrorAlert error={error} />
      </div>
    );
  if (!me)
    return (
      <div className="p-8">
        <Spinner label="Verificando sesión…" />
      </div>
    );

  return (
    <AdminContext.Provider value={me}>
      <DemoContext.Provider value={demo}>
      <TourProvider>
      <WhatsNewHost open={whatsNew.open} onClose={whatsNew.close} />
      <SidebarSlotContext.Provider value={slot}>
        <div className="flex min-h-full">
          {/* Menú lateral fijo (escritorio): navegación global + secciones del estudio actual. */}
          <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col bg-ink text-white md:flex">
            <div className="px-5 pb-4 pt-5">
              <BrandMark />
            </div>
            <nav aria-label="Navegación principal" className="space-y-0.5 px-3" data-tour="nav-main">
              <NavLink to="/dashboard" className={navClass}>
                <Icon name="dashboard" /> Panel
              </NavLink>
              <NavLink to="/estudios" end className={navClass}>
                <Icon name="folder" /> Estudios
              </NavLink>
              <NavLink to="/ajustes" className={navClass}>
                <Icon name="sliders" /> Ajustes
              </NavLink>
              <TourButton variant="sidebar" />
              <button
                type="button"
                onClick={whatsNew.show}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-medium text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                data-testid="whats-new-button"
              >
                <Icon name="info" /> Novedades
              </button>
            </nav>
            <div ref={setSlot} className="mt-5 min-h-0 flex-1 overflow-y-auto px-3" />
            <div className="border-t border-white/10 p-4">
              <p className="truncate font-util text-xs text-white/55" title={me.email}>
                {me.email}
              </p>
              <button type="button" onClick={logout} className="mt-2 flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-sm text-aqua hover:bg-white/5">
                <Icon name="logout" className="h-4 w-4" /> Cerrar sesión
              </button>
            </div>
          </aside>
          <div className="flex min-w-0 flex-1 flex-col">
            {/* Barra compacta en pantallas pequeñas. */}
            <header className="flex flex-wrap items-center justify-between gap-2 bg-ink px-4 py-3 text-white md:hidden">
              <BrandMark size={28} />
              <nav aria-label="Navegación principal (móvil)" className="flex gap-1">
                <NavLink to="/dashboard" className={mobileNavClass}>
                  Panel
                </NavLink>
                <NavLink to="/estudios" className={mobileNavClass}>
                  Estudios
                </NavLink>
                <NavLink to="/ajustes" className={mobileNavClass}>
                  Ajustes
                </NavLink>
              </nav>
              <div className="flex gap-3">
                <TourButton variant="mobile" />
                <button type="button" onClick={logout} className="text-sm text-aqua">
                  Cerrar sesión
                </button>
              </div>
            </header>
            <main className="mx-auto w-full max-w-[1680px] flex-1 px-4 py-5 md:px-7">
              {demo?.enabled && (
                <div className="mb-4">
                  <DemoBanner status={demo} />
                </div>
              )}
              <Outlet />
            </main>
          </div>
        </div>
      </SidebarSlotContext.Provider>
      </TourProvider>
      </DemoContext.Provider>
    </AdminContext.Provider>
  );
}
